import React, { useState, useEffect, useMemo, useCallback } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { Autocomplete, TextField, Box } from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import previousActivityService from '../../../services/admin/previousActivity'
import ProcessTimeline from '../../../../src/components/Dashboard/ProcessTimeline'
import { toast } from 'react-toastify'

const PreviousActivityDialogueBox = ({
  processes,
  open,
  onClose,
  templateRoutes,
  processTemplates,
}) => {
  const [selectedProcess, setSelectedProcess] = useState(null)
  const [activities, setActivities] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [timelineKey, setTimelineKey] = useState(0)
  const [pendingActivityRef, setPendingActivityRef] = useState(null)

  const { user } = useAuthContext()

  useEffect(() => {
    if (open) {
      setSelectedProcess(null)
      setActivities(null)
      setSelectedActivity(null)
      setLoading(false)
      setPendingActivityRef(null)
      setTimelineKey(0)
    }
  }, [open])

  const sortedProcesses = useMemo(
    () => [...(processes || [])].sort((a, b) => (a.prefix || '').localeCompare(b.prefix || '')),
    [processes],
  )

  const stableTemplateRoutes = useMemo(() => templateRoutes, [templateRoutes])
  const stableProcessTemplates = useMemo(() => processTemplates, [processTemplates])

  const referenceOptions = useMemo(
    () =>
      (activities || []).map((item) => ({
        ...item,
        label: `${item.prefix}-${item.process_reference_num}`,
      })),
    [activities],
  )

  useEffect(() => {
    if (!pendingActivityRef || !referenceOptions.length) return

    const match = referenceOptions.find(
      (opt) =>
        opt.process_reference_num === pendingActivityRef.process_reference_num &&
        opt.process_template_id === pendingActivityRef.process_template_id,
    )

    if (match) {
      setSelectedActivity(match)
      setTimelineKey((k) => k + 1)
      setPendingActivityRef(null)
    }
  }, [referenceOptions, pendingActivityRef])

  const fetchActivities = useCallback(
    async (process) => {
      if (!process) return
      try {
        setLoading(true)
        const data = await previousActivityService.getPreviousActivities(user, process.id)
        setActivities(data)
      } catch (error) {
        console.error('Error fetching activities:', error)
      } finally {
        setLoading(false)
      }
    },
    [user],
  )

  const handleProcessChange = async (event, selectedValue) => {
    setSelectedProcess(selectedValue)
    setSelectedActivity(null)
    setActivities([])
    setPendingActivityRef(null)
    setTimelineKey((k) => k + 1)
    if (!selectedValue) return
    fetchActivities(selectedValue)
  }

  const handleActivityChange = (event, selectedValue) => {
    setSelectedActivity(null)
    setTimelineKey((k) => k + 1)
    setTimeout(() => {
      setSelectedActivity(selectedValue)
    }, 0)
  }

  const handleDeleteFromNode = useCallback(
    async (node) => {
      if (!selectedProcess || !selectedActivity) return

      try {
        const result = await previousActivityService.revertFromNode(
          user,
          selectedProcess.id,
          selectedActivity.process_reference_num,
          node.id,
        )

        setPendingActivityRef({
          process_reference_num: selectedActivity.process_reference_num,
          process_template_id: selectedActivity.process_template_id,
        })

        setSelectedActivity(null)
        setActivities(null)

        await fetchActivities(selectedProcess)

        const isSingleNodeDelete = !!result?.deleted_node_id

        if (isSingleNodeDelete) {
          toast.success('Task is deleted successfully')
        } else {
          toast.success('Activity reverted successfully')
        }
      } catch (error) {
        console.error('Error reverting from node:', error)
        toast.error('Failed to revert activity. Please try again.')
      }
    },
    [user, selectedProcess, selectedActivity, fetchActivities],
  )

  return (
    <DialogWrapper heading="Previous Activities" open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: 2,
          px: 0.5,
          minWidth: 240,
        }}
      >
        {/* Process */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontSize: 12, fontWeight: 500 }}>Process</Box>

          <Autocomplete
            fullWidth
            size="small"
            options={sortedProcesses}
            getOptionLabel={(item) =>
              item ? `${item.process_template_name} (${item.prefix})` : ''
            }
            value={selectedProcess}
            onChange={handleProcessChange}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Select Process" />
            )}
          />
        </Box>

        {/* Reference */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontSize: 12, fontWeight: 500 }}>Reference Number</Box>

          <Autocomplete
            key={selectedProcess?.id || 'no-process'}
            fullWidth
            size="small"
            options={referenceOptions}
            getOptionLabel={(item) => item?.label || ''}
            value={selectedActivity}
            onChange={handleActivityChange}
            isOptionEqualToValue={(option, value) =>
              option.process_reference_num === value.process_reference_num &&
              option.process_template_id === value.process_template_id
            }
            disabled={!selectedProcess || loading || !activities?.length}
            noOptionsText={loading ? 'Loading...' : 'No reference numbers found'}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Select Reference Number" />
            )}
          />
        </Box>

        {/* Timeline */}
        {selectedActivity && selectedProcess && activities?.length > 0 && (
          <ProcessTimeline
            key={timelineKey}
            processReferanceNum={selectedActivity.process_reference_num}
            processTemplateId={selectedProcess.id}
            templateRoutes={stableTemplateRoutes}
            processTemplates={stableProcessTemplates}
            autoOpen={true}
            showInTaskCard={false}
            showInTBD={false}
            timelineOnly={true}
            onDeleteFromNode={handleDeleteFromNode}
          />
        )}
      </Box>
    </DialogWrapper>
  )
}

export default PreviousActivityDialogueBox
